import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import Configs from "../config/Configs";
import { ApiResponse } from "../types/ContentsInterface";
import { getPublicImage } from "../utils/imageUtils";

interface MetaData {
  title: string;
  description: string;
  keywords: string;
  imageUrl: string;
  noindex: boolean;
  nofollow: boolean;
  canonicalUrl: string;
}

const MetaDetails: React.FC = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const canonicalUrl = `${window.location.origin}${location.pathname}`;

  const defaultMetaData: MetaData = useMemo(
    () => ({
      title: "Welcome to gimmefy",
      description: "gimmefy",
      keywords: "gimmefy, AI platform",
      imageUrl: "https://gimmefy.ai/server-assets/images/gimmefy-logo.png",
      noindex: false,
      nofollow: false,
      canonicalUrl: canonicalUrl,
    }),
    [canonicalUrl]
  );

  const [metaData, setMetaData] = useState<MetaData>(defaultMetaData);

  // Ensure all metadata values are strings to prevent Helmet errors
  const safeMetaData: MetaData = useMemo(
    () => ({
      title: String(metaData.title || defaultMetaData.title),
      description: String(metaData.description || defaultMetaData.description),
      keywords: String(metaData.keywords || defaultMetaData.keywords),
      imageUrl: String(metaData.imageUrl || defaultMetaData.imageUrl),
      noindex: Boolean(metaData.noindex),
      nofollow: Boolean(metaData.nofollow),
      canonicalUrl: String(
        metaData.canonicalUrl || defaultMetaData.canonicalUrl
      ),
    }),
    [metaData, defaultMetaData]
  );

  // Generate breadcrumb data based on current path
  const generateBreadcrumbData = () => {
    const baseUrl = window.location.origin;
    const pathSegments = pathname.split("/").filter((segment) => segment);

    const breadcrumbItems = [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: baseUrl,
      },
    ];

    let currentPath = "";
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const name =
        segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
      breadcrumbItems.push({
        "@type": "ListItem",
        position: index + 2,
        name: name,
        item: `${baseUrl}${currentPath}`,
      });
    });

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems,
    };
  };

  // Generate FAQ data for pricing and enterprise pages
  const generateFAQData = () => {
    if (pathname.includes("/pricing") || pathname.includes("/enterprise")) {
      return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What pricing plans does gimmefy offer?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "gimmefy offers flexible pricing plans including Starter, Professional, and Enterprise tiers designed to meet the needs of different business sizes and requirements.",
            },
          },
          {
            "@type": "Question",
            name: "Can I upgrade or downgrade my plan anytime?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.",
            },
          },
          {
            "@type": "Question",
            name: "Is there a free trial available?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, gimmefy offers a free trial period so you can explore our features before committing to a paid plan.",
            },
          },
          {
            "@type": "Question",
            name: "What support options are available for Enterprise customers?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Enterprise customers receive priority support, dedicated account management, custom integrations, and advanced security features.",
            },
          },
        ],
      };
    }
    return null;
  };

  // Generate HowTo data for studio pages
  const generateHowToData = () => {
    if (pathname.includes("/studio")) {
      return {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "How to Use gimmefy Studio",
        description:
          "Learn how to create, edit, and manage your content using gimmefy Studio's powerful AI-driven tools.",
        image: "https://gimmefy.ai/server-assets/images/gimmefy-logo.png",
        totalTime: "PT30M",
        estimatedCost: {
          "@type": "MonetaryAmount",
          currency: "USD",
          value: "0",
        },
        supply: [
          {
            "@type": "HowToSupply",
            name: "gimmefy Studio Account",
          },
        ],
        tool: [
          {
            "@type": "HowToTool",
            name: "gimmefy Studio Platform",
          },
        ],
        step: [
          {
            "@type": "HowToStep",
            name: "Access Studio",
            text: "Log into your gimmefy account and navigate to the Studio section.",
            url: "https://gimmefy.ai/studio",
          },
          {
            "@type": "HowToStep",
            name: "Choose Template",
            text: "Select from our library of professional templates or start with a blank canvas.",
            url: "https://gimmefy.ai/studio",
          },
          {
            "@type": "HowToStep",
            name: "Customize Content",
            text: "Use our AI-powered tools to customize your content, add branding, and optimize for your audience.",
            url: "https://gimmefy.ai/studio",
          },
          {
            "@type": "HowToStep",
            name: "Preview and Publish",
            text: "Preview your creation and publish it to your chosen platform or download for offline use.",
            url: "https://gimmefy.ai/studio",
          },
        ],
      };
    }
    return null;
  };

  console.log("generateFAQData()::", generateFAQData());
  console.log("generateHowToData()::", generateHowToData());

  // Generate Article data for blog pages
  const generateArticleData = () => {
    if (pathname.includes("/blog") || pathname.includes("/article")) {
      return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: safeMetaData.title,
        description: safeMetaData.description,
        image: safeMetaData.imageUrl,
        author: {
          "@type": "Organization",
          name: "gimmefy",
          url: "https://gimmefy.ai",
        },
        publisher: {
          "@type": "Organization",
          name: "gimmefy",
          logo: {
            "@type": "ImageObject",
            url: "https://gimmefy.ai/server-assets/images/gimmefy-logo.png",
          },
        },
        datePublished: new Date().toISOString(),
        dateModified: new Date().toISOString(),
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": safeMetaData.canonicalUrl,
        },
      };
    }
    return null;
  };

  useEffect(() => {
    const params = {
      "filters[group_name]": Configs.CONTENTS.GROUP_NAME.SEO_DETAILS,
      "filters[name]": pathname,
    };

    const loadSEOData = async () => {
      try {
        const response = await axios.get<ApiResponse>(
          `${Configs.API.BASE_URL}/contents/public`,
          { params }
        );
        const data = response.data;
        const rawMetaData = data?.payload?.[0]?.content || "{}";
        const metaData = JSON.parse(rawMetaData);

        const imageUrl =
          getPublicImage("frontend-files", metaData?.image) ||
          defaultMetaData.imageUrl;

        setMetaData({
          ...defaultMetaData,
          title: metaData.meta_title || defaultMetaData.title,
          description: metaData.meta_description || defaultMetaData.description,
          keywords: metaData.meta_keywords || defaultMetaData.keywords,
          imageUrl: imageUrl,
        });
      } catch (error) {
        console.error("Failed to fetch or parse metadata:", error);
        setMetaData(defaultMetaData);
      }
    };
    loadSEOData();
  }, [location, pathname, defaultMetaData]);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{safeMetaData.title}</title>
      <meta name="description" content={safeMetaData.description} />
      <meta name="keywords" content={safeMetaData.keywords} />

      {/* Robots Meta */}
      {safeMetaData.noindex && <meta name="robots" content="noindex" />}
      {safeMetaData.nofollow && <meta name="robots" content="nofollow" />}
      {!safeMetaData.noindex && !safeMetaData.nofollow && (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      {/* Canonical URL */}
      {safeMetaData.canonicalUrl && (
        <link rel="canonical" href={safeMetaData.canonicalUrl} />
      )}

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={safeMetaData.title} />
      <meta property="og:description" content={safeMetaData.description} />
      <meta property="og:image" content={safeMetaData.imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={safeMetaData.title} />
      {safeMetaData.canonicalUrl && (
        <meta property="og:url" content={safeMetaData.canonicalUrl} />
      )}
      <meta property="og:site_name" content="gimmefy" />

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@gimmefy" />
      <meta name="twitter:title" content={safeMetaData.title} />
      <meta name="twitter:description" content={safeMetaData.description} />
      <meta name="twitter:image" content={safeMetaData.imageUrl} />
      <meta name="twitter:image:alt" content={safeMetaData.title} />

      {/* Additional Meta Tags */}
      <meta name="author" content="gimmefy" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {/* Enhanced Organization Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "gimmefy",
          url: "https://gimmefy.ai",
          logo: "https://gimmefy.ai/server-assets/images/gimmefy-logo.png",
          description: safeMetaData.description,
          foundingDate: "2022",
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer service",
            url: "https://gimmefy.ai/contact",
          },
          sameAs: [
            "https://www.linkedin.com/company/gimmefy-ai",
            "https://www.g2.com/products/gimmefy-ai",
            "https://www.imda.gov.sg/resources/innovative-tech-companies-directory/gimmefy",
            "https://www.youtube.com/@gimmefy",
            "https://www.instagram.com/gimmefy.ai/",
            "https://www.facebook.com/people/Gimmefyai/61565941284952/",
          ],
        })}
      </script>

      {/* BreadcrumbList Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(generateBreadcrumbData())}
      </script>

      {/* FAQPage Structured Data for Pricing/Enterprise pages */}
      {/* {generateFAQData() && (
                <script type="application/ld+json">
                    {JSON.stringify(generateFAQData())}
                </script>
            )} */}

      {/* HowTo Structured Data for Studio pages */}
      {/* {generateHowToData() && (
                <script type="application/ld+json">
                    {JSON.stringify(generateHowToData())}
                </script>
            )} */}

      {/* Article Structured Data for Blog pages */}
      {generateArticleData() && (
        <script type="application/ld+json">
          {JSON.stringify(generateArticleData())}
        </script>
      )}

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Shalu Wasu",
          url: "https://www.linkedin.com/in/shaluwasu/",
          jobTitle: "Co-Founder, gimmefy",
          worksFor: {
            "@type": "Organization",
            name: "gimmefy",
            url: "https://gimmefy.ai",
          },
          sameAs: ["https://www.linkedin.com/in/shaluwasu/"],
          knowsAbout: [
            "AI Marketing",
            "Content Strategy",
            "Technology Leadership",
          ],
        })}
      </script>
    </Helmet>
  );
};

export default MetaDetails;
